import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  IconButton,
  Typography,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-hot-toast";
import { baseAxios } from "../../utils/config";
import {
  CONTROL_RADIUS,
  dialogPaperSx,
  dialogTitleSx,
  dialogCloseIconSx,
  primaryBtnSx,
  fieldSx,
} from "./schedulerDialogStyles";

/**
 * Cancel-appointment dialog. Reasons are loaded from the API each time the
 * dialog opens (JD-410: admins manage them under Manage Cancellation Reasons).
 * Emits `onConfirm({ reasonId })` with a numeric id.
 */
const CancelModal = ({ open, onClose, onConfirm }) => {
  const [reasonId, setReasonId] = useState("");
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setReasonId("");
    setLoading(true);
    baseAxios
      .get("/cancellation-reasons", { params: { is_active: "true" } })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.reasons;
        setReasons(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (cancelled) return;
        setReasons([]);
        toast.error("Could not load cancellation reasons.", { duration: 3000 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleConfirm = () => {
    onConfirm({ reasonId: Number(reasonId) });
    onClose();
  };

  const noReasons = !loading && reasons.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography fontWeight={700} color="inherit" sx={{ fontSize: "1.1rem" }}>
          Select a reason for cancellation
        </Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          sx={dialogCloseIconSx}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Select
          value={reasonId}
          onChange={(e) => setReasonId(e.target.value)}
          fullWidth
          displayEmpty
          size="small"
          disabled={loading || noReasons}
          sx={{
            mt: 1,
            borderRadius: CONTROL_RADIUS,
            ...fieldSx,
          }}
          renderValue={(value) => {
            if (loading) return "Loading reasons…";
            if (value === "") return "Select a reason";
            return reasons.find((r) => r.id === value)?.name ?? "";
          }}
          endAdornment={
            loading ? (
              <CircularProgress size={16} sx={{ mr: 3 }} />
            ) : null
          }
        >
          <MenuItem value="" disabled>
            Select a reason
          </MenuItem>
          {reasons.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </Select>
        {noReasons && (
          <FormHelperText error sx={{ ml: 0.5 }}>
            No active reasons — add one in Admin Panel → Manage Cancellation
            Reasons.
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={reasonId === "" || loading}
          sx={primaryBtnSx}
        >
          Cancel Appointment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelModal;
